/**
 * Sign-out, as a decision over the outbox (plan D23). Pure over injected
 * ports, so it is tested in Node; `session.tsx` wraps it for React.
 */
import {
  itemsOf,
  type OutboxItem,
  type OutboxState,
} from "@repo/client/offline";

export interface SessionPorts {
  /** Which account this device holds, and how to forget it (`account.ts`). */
  readonly accountScope: {
    current(): string | null;
    leave(): Promise<void>;
  };
  readonly api: {
    hasSession(): Promise<boolean>;
    signOut(): Promise<void>;
  };
  /** Clears the in-memory Query cache and the persisted one. */
  clearCaches(): Promise<void> | void;
  readonly outbox: {
    dropEntity(entityId: string): Promise<OutboxState>;
    getState(): OutboxState;
    /** Memoised: resolves to the FIRST load's state, so read `getState()`. */
    load(): Promise<unknown>;
  };
}

export type SignOutResult =
  | { readonly needsConfirm: true; readonly pending: number }
  | { readonly needsConfirm: false; readonly signedOut: true };

export interface SignOutOptions {
  /** The person confirmed that the unsynced writes are to be lost. */
  readonly force?: boolean;
}

/** Every session the given items hold a write for. */
function sessionsIn(items: readonly OutboxItem[]): string[] {
  return [...new Set(items.map((item) => item.dependsOn ?? item.entityId))];
}

export function createSessionController(ports: SessionPorts) {
  return {
    /**
     * Signing out with unsynced workout writes loses them, so it is refused
     * until the person confirms: `{ needsConfirm: true, pending }`, where
     * `pending` counts every unsynced item OF THIS ACCOUNT, quarantined ones
     * included (they are lost too). The UI asks, then calls
     * `signOut({ force: true })`.
     *
     * Forced, THIS account's writes are dropped BEFORE the server sign-out:
     * left in place, they would replay under whoever signs in next on this
     * device. Another account's are not touched — they are that person's data
     * and drain when they sign in again (T28/T34) — so both the count and the
     * drop are `itemsOf(state, scope)`.
     *
     * With no account known (`current()` is `null`), that set is empty and
     * nothing is dropped. The window is the async scope load at boot, and a
     * sign-out needs a rendered profile screen, which needs `me`, which claims
     * the account (`session.tsx`).
     *
     * Unforced with nothing of this account's queued, the outbox is not
     * touched at all. `leave()` runs last, after the caches: its listeners are
     * the per-account cleanup (T35), and they must not race a cache clear.
     */
    async signOut(options: SignOutOptions = {}): Promise<SignOutResult> {
      await ports.outbox.load();
      const mine = itemsOf(ports.outbox.getState(), {
        accountId: ports.accountScope.current(),
      });
      if (mine.length > 0) {
        if (options.force !== true) {
          return { needsConfirm: true, pending: mine.length };
        }
        for (const sessionId of sessionsIn(mine)) {
          await ports.outbox.dropEntity(sessionId);
        }
      }
      await ports.api.signOut();
      await ports.clearCaches();
      await ports.accountScope.leave();
      return { needsConfirm: false, signedOut: true };
    },
  };
}
